import { useEffect, useReducer, useRef } from 'react';
import type { ReactNode } from 'react';

import type { EntryCardPayoutProps, EntryStatus } from '@/components/entry-card';
import type { CashOutLabels, EntryCashOutControlsProps } from '@/components/entry-card';
import { ds } from '@/components/lib/design.system';
import {
    cashOutReducer,
    initialCashOutState,
    selectCashOutPayoutView,
    selectCashOutStatus,
} from '@/utils/entry-card/entry-cash-out-reducer';

/** `success` carries the settled entry; `offerChange` means the backend re-priced mid-flight. Other failures throw. */
export type CashOutResult<TEntry> = { type: 'success'; entry: TEntry } | { type: 'offerChange'; amount: number };

export type EntryCashOutAdapter<TEntry> = {
    /** Eligible at all, independent of a live offer. */
    canCashOut: boolean;
    /** `null` while loading/suspended. */
    offer: number | null;
    cashedOutAmount: (settled: TEntry | null) => number;
    confirmCashOut: (amount: number) => Promise<CashOutResult<TEntry>>;
    /** Pass the pin only while the entry still reads open - the machine never expires it. */
    cachedCashedOut: TEntry | null;
    markCashedOut: (entry: TEntry) => void;
    /** `retry` re-attempts at the same price. */
    onError: (error: unknown, retry: () => void) => void;
    /** Backend re-priced mid-flight; `complete` cashes out at the new price. */
    onOfferChange: (args: { previous: number; next: number; complete: () => void }) => void;
    formatCurrency: (amount: number) => string;
    labels: { controls: CashOutLabels; payoutColumn: Record<'payout' | 'cash_out', string> };
    payoutIcon?: ReactNode;
    currencyIcon?: ReactNode;
};

/** Open-card overrides; all absent when not eligible. `controls` being set is the signal to render the button region. */
export type EntryCashOut = {
    status?: EntryStatus;
    payout?: Partial<EntryCardPayoutProps>;
    controls?: EntryCashOutControlsProps;
};

/**
 * Drives an open entry card's inline cash-out: the phase machine, offer, mutation, and in-place confirmed
 * state. Backend-agnostic — all product specifics come from the injected `adapter`.
 */
export const useEntryCashOut = <TEntry>(adapter: EntryCashOutAdapter<TEntry>): EntryCashOut => {
    const [state, dispatch] = useReducer(cashOutReducer<TEntry>, adapter.cachedCashedOut, initialCashOutState<TEntry>);

    const effectiveOffer = state.offerOverride ?? adapter.offer ?? 0;

    // Guard against a same-frame double-tap firing two real-money cash-out requests: the second settles as
    // already-cashed-out and pops a spurious error over the confirmed card.
    const inFlight = useRef(false);

    // The lazy initializer only runs at mount. If a cash-out happens elsewhere (e.g. the detail screen)
    // while this card is already mounted, the reducer stays idle.
    // If we have item inside cache, that means the state should be 'confirmed'
    useEffect(() => {
        if (adapter.cachedCashedOut !== null && state.phase !== 'confirmed') {
            dispatch({ type: 'SUCCESS', entry: adapter.cachedCashedOut });
        }
    }, [adapter.cachedCashedOut, state.phase]);

    // A background refresh can re-price the offer while the user sits on the confirm screen. Never cash out
    // at a price they never confirmed: drop to idle at the new price so they re-confirm. Mirrors OFFER_CHANGE.
    useEffect(() => {
        if (state.phase === 'confirm' && adapter.offer != null && adapter.offer !== state.offerOverride) {
            dispatch({ type: 'CONFIRM_OFFER_CHANGED' });
        }
    }, [state.phase, state.offerOverride, adapter.offer]);

    // Confirm, Retry and Complete share one attempt. `amount` is passed explicitly so a price-change
    // "Complete" cashes out at the new price rather than a stale closed-over offer.
    const attemptCashOut = async (amount: number) => {
        // Never cash out for a non-positive amount — guards against a suspended offer (amount 0)
        // slipping through to a real-money POST.
        if (inFlight.current || amount <= 0) {
            return;
        }
        inFlight.current = true;
        dispatch({ type: 'SUBMIT', offer: amount });
        try {
            const result = await adapter.confirmCashOut(amount);
            // A re-priced offer never auto-cashes; drop to idle and let the adapter's price-change sheet re-attempt.
            if (result.type === 'offerChange') {
                dispatch({ type: 'OFFER_CHANGE' });
                adapter.onOfferChange({
                    previous: amount,
                    next: result.amount,
                    complete: () => attemptCashOut(result.amount),
                });
                return;
            }
            adapter.markCashedOut(result.entry);
            dispatch({ type: 'SUCCESS', entry: result.entry });
        } catch (error) {
            dispatch({ type: 'ERROR' });
            adapter.onError(error, () => attemptCashOut(amount));
        } finally {
            inFlight.current = false;
        }
    };

    // A cash-out needs a real amount to act on. The live offer suspends on resume/focus refetches
    // (adapter.offer → null), collapsing effectiveOffer to 0; the confirm/loading pin keeps it positive.
    const hasActionableOffer = effectiveOffer > 0;

    const onCashOut = () => dispatch({ type: 'PRESS_CASH_OUT', offer: effectiveOffer });
    const onCancel = () => dispatch({ type: 'CANCEL' });
    const onConfirm = () => {
        if (hasActionableOffer) {
            attemptCashOut(effectiveOffer);
        }
    };

    // Active phases stay rendered even when eligibility flips underneath — e.g. the entry-detail
    // cache settles (can_be_cashed_out → false) before SUCCESS lands, or the offer suspends on a
    // resume refetch while the user is on confirm. Only idle requires an actionable offer on an
    // open entry.
    const eligible = state.phase !== 'idle' || (adapter.canCashOut && hasActionableOffer);
    if (!eligible) {
        return {};
    }

    // Idle keeps the card's own base payout; only the active phases override the column.
    const payoutView =
        state.phase === 'idle'
            ? undefined
            : selectCashOutPayoutView(state.phase, {
                  offer: effectiveOffer,
                  cashedOutAmount: adapter.cashedOutAmount(state.cashedOut),
              });

    return {
        status: selectCashOutStatus(state.phase),
        payout: payoutView && {
            payoutLabel: adapter.labels.payoutColumn[payoutView.labelKey],
            payoutAmount: adapter.formatCurrency(payoutView.amount),
            payoutIcon: adapter.payoutIcon,
            payoutColor: payoutView.bright ? ds.colorToken.text.primary : 'neutral300',
        },
        controls: {
            phase: state.phase,
            labels: adapter.labels.controls,
            offerAmount: adapter.formatCurrency(effectiveOffer),
            currencyIcon: adapter.currencyIcon,
            // Confirm is inert while the offer is suspended, so the button reflects that it can't act.
            confirmDisabled: !hasActionableOffer,
            onCashOut,
            onCancel,
            onConfirm,
        },
    };
};
